/**
 * LinkedIn Setup Component
 *
 * Allows users to authenticate with LinkedIn to enable profile enrichment.
 * Primary method: Opens a browser window where user logs in directly.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  linkedInExtractCookie,
  linkedInCancelBrowser,
  linkedInSubmitManualCookie,
  getLinkedInStatus,
  validateLinkedInConnection,
  linkedInLogout,
  type LinkedInAuthStatus,
  type LinkedInValidationResponse,
} from '../../services/api';

interface LinkedInSetupProps {
  compact?: boolean;
  onStatusChange?: (authenticated: boolean) => void;
}

export function LinkedInSetup({ compact = false, onStatusChange }: LinkedInSetupProps) {
  const [status, setStatus] = useState<LinkedInAuthStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Setup flow state
  const [showSetup, setShowSetup] = useState(false);
  const [browserSessionId, setBrowserSessionId] = useState<string | null>(null);
  const [extracting, setExtracting] = useState(false);

  // Manual cookie input state
  const [manualCookie, setManualCookie] = useState('');
  const [cookieLoading, setCookieLoading] = useState(false);

  // Validation state
  const [validating, setValidating] = useState(false);
  const [validationResult, setValidationResult] = useState<LinkedInValidationResponse | null>(null);

  // Fetch initial status
  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      setLoading(true);
      const authStatus = await getLinkedInStatus();
      setStatus(authStatus);
      onStatusChange?.(authStatus.authenticated);

      // Auto-validate if needed and authenticated
      if (authStatus.authenticated && authStatus.needs_validation) {
        handleValidate();
      }
    } catch {
      setError('Failed to check LinkedIn status');
    } finally {
      setLoading(false);
    }
  };

  const handleValidate = async () => {
    setValidating(true);
    setValidationResult(null);
    setError(null);

    try {
      const result = await validateLinkedInConnection();
      setValidationResult(result);

      if (!result.valid) {
        setError(result.error || 'LinkedIn session is no longer valid. Please reconnect.');
      } else {
        setSuccessMessage(
          `LinkedIn connection verified! Connected as ${result.profile_name || 'LinkedIn User'}`
        );
      }

      // Refresh status to get updated validation timestamp
      const authStatus = await getLinkedInStatus();
      setStatus(authStatus);
      onStatusChange?.(authStatus.authenticated && result.valid);
    } catch {
      setError('Failed to validate LinkedIn connection');
    } finally {
      setValidating(false);
    }
  };

  const handleExtractCookie = async () => {
    if (!browserSessionId) return;

    setError(null);
    setExtracting(true);

    try {
      const response = await linkedInExtractCookie({ session_id: browserSessionId });

      if (response.success) {
        setSuccessMessage('LinkedIn connected successfully!');
        setBrowserSessionId(null);
        setShowSetup(false);
        fetchStatus();
      } else {
        // Don't close browser - let user try again
        setError(
          response.error || 'Could not detect login. Please complete the login and try again.'
        );
      }
    } catch {
      setError('Failed to verify login. Please try again.');
    } finally {
      setExtracting(false);
    }
  };

  const handleCancelBrowser = async () => {
    if (browserSessionId) {
      try {
        await linkedInCancelBrowser({ session_id: browserSessionId });
      } catch {
        // Ignore errors on cancel
      }
    }
    resetForm();
  };

  const handleLogout = async () => {
    try {
      await linkedInLogout();
      fetchStatus();
    } catch {
      setError('Failed to log out');
    }
  };

  const resetForm = useCallback(() => {
    setShowSetup(false);
    setBrowserSessionId(null);
    setExtracting(false);
    setManualCookie('');
    setError(null);
    setSuccessMessage(null);
  }, []);

  const handleManualCookieSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setCookieLoading(true);

    try {
      const response = await linkedInSubmitManualCookie({
        cookie: manualCookie,
      });

      if (response.success) {
        resetForm();
        fetchStatus();
      } else {
        setError(response.error || 'Failed to save cookie');
      }
    } catch {
      setError('Failed to save cookie. Please check the format and try again.');
    } finally {
      setCookieLoading(false);
    }
  };

  if (loading) {
    // In compact mode, show nothing while checking (avoids distracting spinner in Leadership tab)
    if (compact) return null;
    return (
      <div className="flex items-center gap-2 text-gray-500">
        <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
        <span>Checking LinkedIn status...</span>
      </div>
    );
  }

  // Compact view for Leadership tab prompt
  if (compact) {
    if (status?.authenticated) {
      return (
        <div className="flex items-center gap-2 text-sm text-green-600">
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
              clipRule="evenodd"
            />
          </svg>
          <span>LinkedIn connected</span>
        </div>
      );
    }

    return (
      <button
        onClick={() => setShowSetup(true)}
        className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700"
      >
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
        </svg>
        <span>Connect LinkedIn for richer profiles</span>
      </button>
    );
  }

  // Full settings view
  return (
    <div className="bg-white rounded-lg border p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 bg-[#0077b5] rounded flex items-center justify-center">
          <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
          </svg>
        </div>
        <div>
          <h3 className="text-lg font-semibold">LinkedIn Integration</h3>
          <p className="text-sm text-gray-500">
            Connect your LinkedIn account to enrich executive profiles
          </p>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
          {error}
        </div>
      )}

      {successMessage && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded text-green-700 text-sm">
          {successMessage}
        </div>
      )}

      {status?.authenticated ? (
        <div className="space-y-4">
          {/* Connection status */}
          {validationResult?.valid === false || status.validation_error ? (
            <div className="flex items-center gap-2 text-amber-600">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="font-medium">Connection Issue</span>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-green-600">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="font-medium">Connected</span>
            </div>
          )}

          {/* Validation error warning */}
          {(validationResult?.valid === false || status.validation_error) && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded text-amber-800 text-sm">
              <p className="font-medium">Session needs to be refreshed</p>
              <p className="mt-1">{validationResult?.error || status.validation_error}</p>
              <p className="mt-2 text-xs">Please disconnect and reconnect your LinkedIn account.</p>
            </div>
          )}

          {status.user_email && (
            <p className="text-sm text-gray-600">Account: {status.user_email}</p>
          )}

          {status.expires_at && (
            <p className="text-sm text-gray-500">
              Session expires: {new Date(status.expires_at).toLocaleDateString()}
            </p>
          )}

          <div className="flex gap-2">
            <button
              onClick={handleValidate}
              disabled={validating}
              className="px-4 py-2 text-sm text-blue-600 border border-blue-200 rounded hover:bg-blue-50 disabled:opacity-50 flex items-center gap-2"
            >
              {validating ? (
                <>
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                  Validating...
                </>
              ) : (
                'Verify Connection'
              )}
            </button>
            <button
              onClick={handleLogout}
              className="px-4 py-2 text-sm text-red-600 border border-red-200 rounded hover:bg-red-50"
            >
              Disconnect LinkedIn
            </button>
          </div>
        </div>
      ) : showSetup ? (
        <div className="space-y-4">
          {browserSessionId ? (
            /* Browser is open - waiting for user to log in */
            <div className="space-y-4">
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0">
                    <svg
                      className="w-8 h-8 text-blue-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-medium text-blue-900">Browser Window Opened</h4>
                    <p className="text-sm text-blue-700 mt-1">
                      A browser window should have opened with LinkedIn's login page. Please log in
                      to your LinkedIn account in that window.
                    </p>
                    <p className="text-sm text-blue-700 mt-2">
                      <strong>After you complete the login</strong> and see LinkedIn's feed, click
                      the button below to finish setup.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleExtractCookie}
                  disabled={extracting}
                  className="px-4 py-2 bg-[#0077b5] text-white rounded hover:bg-[#006097] disabled:opacity-50 flex items-center gap-2"
                >
                  {extracting ? (
                    <>
                      <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      Checking...
                    </>
                  ) : (
                    <>
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      I've Logged In
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={handleCancelBrowser}
                  disabled={extracting}
                  className="px-4 py-2 text-gray-600 border rounded hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>

              <p className="text-xs text-gray-500">
                If the browser window didn't open, check if it's behind other windows or try the
                manual method below.
              </p>
            </div>
          ) : (
            /* Simple 2-step flow */
            <div className="space-y-6">
              {/* Step 1: Open LinkedIn */}
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-8 h-8 bg-[#0077b5] text-white rounded-full flex items-center justify-center text-lg font-bold">
                    1
                  </span>
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900 mb-1">Log in to LinkedIn</h4>
                    <p className="text-sm text-gray-600 mb-3">
                      Click below to open LinkedIn. Log in with your credentials and complete any
                      verification.
                    </p>
                    <button
                      type="button"
                      onClick={() => window.open('https://www.linkedin.com/login', '_blank')}
                      className="px-5 py-2.5 bg-[#0077b5] text-white rounded-lg hover:bg-[#006097] flex items-center gap-2 font-medium shadow-sm"
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                      </svg>
                      Open LinkedIn
                    </button>
                  </div>
                </div>
              </div>

              {/* Step 2: Link Account */}
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-8 h-8 bg-gray-400 text-white rounded-full flex items-center justify-center text-lg font-bold">
                    2
                  </span>
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900 mb-1">Link your account</h4>
                    <p className="text-sm text-gray-600 mb-3">
                      After logging in, copy your session cookie from the browser and paste it
                      below.
                    </p>

                    <details className="mb-3">
                      <summary className="text-sm text-blue-600 cursor-pointer hover:text-blue-800">
                        How to find your session cookie
                      </summary>
                      <div className="mt-2 ml-2 text-xs text-gray-600 space-y-1 bg-white p-3 rounded border">
                        <p>In the LinkedIn browser tab:</p>
                        <ol className="list-decimal list-inside space-y-1">
                          <li>
                            Press <kbd className="px-1.5 py-0.5 bg-gray-100 rounded">F12</kbd> to
                            open Developer Tools
                          </li>
                          <li>
                            Click <strong>Application</strong> tab (or Storage in Firefox)
                          </li>
                          <li>
                            Click <strong>Cookies</strong> → <strong>linkedin.com</strong>
                          </li>
                          <li>
                            Find <strong>li_at</strong>, double-click Value, copy it
                          </li>
                        </ol>
                      </div>
                    </details>

                    <textarea
                      value={manualCookie}
                      onChange={(e) => setManualCookie(e.target.value)}
                      placeholder="Paste li_at cookie value here..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      rows={2}
                    />

                    <div className="flex items-center gap-3 mt-3">
                      <button
                        type="button"
                        onClick={handleManualCookieSubmit}
                        disabled={!manualCookie || manualCookie.length < 100 || cookieLoading}
                        className="px-5 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 font-medium shadow-sm"
                      >
                        {cookieLoading ? (
                          <>
                            <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              />
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                              />
                            </svg>
                            Connecting...
                          </>
                        ) : (
                          <>
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                              />
                            </svg>
                            Link Account
                          </>
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={resetForm}
                        className="px-4 py-2.5 text-gray-600 hover:text-gray-800"
                      >
                        Cancel
                      </button>
                    </div>

                    {manualCookie && manualCookie.length > 0 && manualCookie.length < 100 && (
                      <p className="mt-2 text-xs text-amber-600">
                        Cookie seems too short. The li_at cookie is typically 200+ characters.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Connect your LinkedIn account to automatically enrich executive profiles with career
            history, education, and other professional details.
          </p>
          <button
            onClick={() => setShowSetup(true)}
            className="px-4 py-2 bg-[#0077b5] text-white rounded hover:bg-[#006097] flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
            </svg>
            Connect LinkedIn
          </button>
        </div>
      )}
    </div>
  );
}

export default LinkedInSetup;
